import { FormattedMessage, useIntl } from 'react-intl';
import { ResponsiveLine } from '@nivo/line';
import { Button, Form, Modal, Popover, Skeleton, Typography } from 'antd';
import { useEffect, useState } from 'react';

import { currencyFormatter, metricPrefixFormatter } from '@/utils/number';

import { ContentWrapper, EllipsisButton } from '@/components/_common';

import { ContentWrapperProps } from '@/components/_common/ContentWrapper';
import { useChartTheme, useConvertChartData, useDisclosure } from '@/hooks';
import {
  ExpenseIncomeFormValues,
  ExpensesIncomeParams,
} from '@/core/domain/workorder/ManagementCharts/ManagementChart.types';
import FinancialChartModalForm from './FinancialChartModalForm';
import moment from 'moment';
import { useChartExpensesIncome } from '@/core/domain/workorder/ManagementCharts/ManagementChart.hooks';
import { DateTimeFormat } from '@/utils/moment';
import RedirectIcon from '@/components/_common/RedirectIcon';
import { buildParamsStr } from '@/utils/queryParams';
import { Group } from 'antd/lib/avatar';
import { Info } from 'react-feather';

const { Title, Paragraph } = Typography;

function FinancialChart({ ...props }: ContentWrapperProps) {
  const [form] = Form.useForm<ExpenseIncomeFormValues>();
  const [modalVisible, modalVisibleAction] = useDisclosure(false);
  const intl = useIntl();
  const chartTheme = useChartTheme();

  const [filter, setFilter] = useState<ExpensesIncomeParams>({
    startDate: moment().subtract(5, 'months').format(DateTimeFormat),
    endDate: moment().format(DateTimeFormat),
  });

  const { data, mutateAsync, isLoading } = useChartExpensesIncome();

  const chartData = useConvertChartData(data?.chartData);

  useEffect(() => {
    mutateAsync(filter);
  }, []);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const obj: ExpensesIncomeParams = {
        companiesIds: values.companiesIds,
        startDate: values.dates[0].format('YYYY-MM-DD'),
        endDate: values.dates[1].format('YYYY-MM-DD'),
      };

      setFilter(obj);

      await mutateAsync(obj);

      modalVisibleAction.close();
    } catch {}
  };

  const handleModalReset = async () => {
    const startDate = moment().subtract(5, 'months');
    const endDate = moment();

    const obj = {
      startDate: startDate.format(DateTimeFormat),
      endDate: endDate.format(DateTimeFormat),
    };

    setFilter(obj);

    await mutateAsync(obj);

    modalVisibleAction.close();
    form.resetFields();
  };

  const paramsRedirectReport = {
    initialDate: filter.startDate,
    finalDate: filter.endDate,
    companiesIds: filter.companiesIds,
    lsSituacao: ['SETTLED'],
    dateType: 'PAY',
    reportType: 'PAY',
  };

  return (
    <>
      <ContentWrapper
        style={{
          height: '360px',
          padding: '24px',
        }}
        extra={
          <Group style={{ alignItems: 'center' }}>
            <Popover
              title="Relação entre Receitas/Despesas (R$)"
              placement="right"
              content={
                <div>
                  <p>
                    Os valores apresentados nesse gráfico são líquidos, já
                    descontadas as tarifas correspondentes.
                  </p>

                  <p>
                    Para consultar os valores brutos, utilize o Relatório de
                    Contas a Receber.
                  </p>
                </div>
              }
            >
              <Info size={18} color="#364068" />
            </Popover>

            <EllipsisButton onClick={modalVisibleAction.open} />

            <RedirectIcon
              placement="bottom"
              paths={[
                {
                  title: 'Relatório das Despesas',
                  url: `/app/reports/financial/billsToPay?${buildParamsStr(
                    paramsRedirectReport,
                  )}`,
                },
                {
                  title: 'Relatório das Receitas',
                  url: `/app/reports/financial/billsToReceive?${buildParamsStr(
                    paramsRedirectReport,
                  )}`,
                },
              ]}
            />
          </Group>
        }
        {...props}
      >
        <Skeleton loading={isLoading}>
          <>
            <Title
              level={5}
              style={{ textAlign: 'center', marginBottom: '4px' }}
            >
              <FormattedMessage id="dashboard.financialReport" />
            </Title>
            <Paragraph style={{ textAlign: 'center', margin: '4px 0 4px 0' }}>
              <FormattedMessage id="dashboard.lastSixMonths" />
            </Paragraph>

            <ResponsiveLine
              data={chartData ? chartData : []}
              enableSlices="x"
              margin={{ top: 15, right: 25, bottom: 90, left: 70 }}
              xScale={{ type: 'point' }}
              yScale={{
                type: 'linear',
                min: 0,
                max: 'auto',
                reverse: false,
              }}
              yFormat={(value) => `${currencyFormatter(value)}`}
              curve="monotoneX"
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                legendOffset: 36,
                legendPosition: 'middle',
              }}
              axisLeft={{
                tickSize: 5,
                tickPadding: 5,
                tickRotation: 0,
                tickValues: 5,
                legend: intl.formatMessage({
                  id: 'dashboard.financialReport.incomeAndExpensesRelation',
                }),
                format: (value) => `${metricPrefixFormatter(+value)}`,
                legendOffset: -50,
                legendPosition: 'middle',
              }}
              enableGridX={false}
              pointColor={{ theme: 'background' }}
              pointBorderWidth={2}
              pointBorderColor={{ from: 'serieColor' }}
              pointLabelYOffset={-12}
              useMesh
              theme={chartTheme}
            />
          </>
        </Skeleton>
      </ContentWrapper>

      <Modal
        title="Filtros"
        open={modalVisible}
        onCancel={modalVisibleAction.close}
        width={400}
        onOk={handleOk}
        footer={[
          <Button type="primary" ghost onClick={handleModalReset}>
            Resetar
          </Button>,
          <Button type="primary" onClick={handleOk} loading={isLoading}>
            Atualizar gráfico
          </Button>,
        ]}
      >
        <FinancialChartModalForm form={form} />
      </Modal>
    </>
  );
}

export default FinancialChart;
